<template>
  <div
    class="d-flex justify-content-end mb-2"
    v-if="!loading"
  >
    <template v-if="unreadCount > 0">
      {{ t('unreadSummary', { scope: showAll ? t('scopeAll') : t('scopeRecent') }, unreadCount) }}
    </template>
    <template v-else>
      {{ t('noUnreadSummary', { scope: showAll ? t('scopeAll') : t('scopeRecent') }) }}
    </template>
  </div>
  <div
    v-if="!showAll && hasMore"
    class="d-flex justify-content-end mb-2"
  >
    <button
      class="btn btn-sm btn-outline-warning"
      @click="loadAll"
    >
      {{ t('allMessages') }}
    </button>
  </div>
  <div
    class="messages-block"
    v-if="model.data?.length"
  >
    <div
      class="one-mess"
      v-for="item in model.data"
      :key="item.id"
      :ref="el => setRowRef(el, item)"
    >
      <div
        :class="item.cl"
        class="card mb-2"
      >
        <div class="text-mess">
          <span
            v-if="!item.was_read"
            class="new-mess"
            >{{ t('badgeNew') }}</span
          >
          <div class="only-text-mess p-2">
            <div v-html="item.message" />
            <div class="footer-mess d-flex justify-content-between align-items-center">
              <span
                v-if="item.created_at"
                class="created-mess fs-12"
                >{{ item.created_at }}</span
              >
              <RouterLink
                v-if="item.ref_link"
                :to="item.ref_link"
                class="link-mess"
                active-class=""
                @click="emit('modalstack:close')"
              >
                {{ t('goToLink') }}
              </RouterLink>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="!showAll && hasMore"
      class="text-center mt-3"
    >
      <button
        class="btn btn-sm btn-outline-secondary"
        @click="loadAll"
      >
        {{ t('allMessages') }}
      </button>
    </div>
  </div>
  <div v-else-if="loading">
    <div class="text-center py-5">
      <i class="bi bi-arrow-clockwise spinning"></i>
      <div class="mt-2">{{ t('loadingMessages') }}</div>
    </div>
  </div>
  <div v-else>{{ t('noMessages') }}</div>
</template>

<script setup>
import { onMounted, ref, computed, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import dompurify from 'dompurify'
import { commons } from '@/lib/transformer'
import { useApi } from 'soykajs/composables/api'

const { t, locale } = useI18n()

const ids = new Set()
const observer = ref(null)
const clLevels = { 1: 'danger', 2: 'warning', 3: 'primary' }
const showAll = ref(false)
const loading = ref(true)

const { model, refresh } = useApi({
  url: 'notifications',
  data: {},
  pick: d => {
    d.data = d.data.map(d => {
      if (d.created_at) {
        d.created_at = commons.dateLocal(d.created_at, 'Pp')
      }
      d.cl = clLevels[d.level]
      d.message = dompurify.sanitize(d.message)
      return d
    })
    loading.value = false
    return d
  },
})

// Язык ленты backend берёт из заголовка X-Locale (useApi, хук locale в main.js)
const notificationQuery = () => (showAll.value ? { mode: 'all' } : {})

const { refresh: doRead } = useApi({
  url: 'notifications/read',
  method: 'put',
  data: {},
  transform: () => ({ notifications: Array.from(ids.values()).map(id => ({ id })) }),
})

const hasMore = computed(() => {
  const total = model.value?.total_count || 0
  return total > 200
})

const unreadCount = computed(() => {
  return model.value?.data?.filter(message => !message.was_read).length || 0
})

const emit = defineEmits(['modalstack:close'])
const props = defineProps({
  modalstack: {
    type: Boolean,
    default: false,
  },
  class: {
    type: String,
    default: '',
  },
  headerText: {
    type: String,
    default: '',
  },
  localHasUnread: {
    type: [Boolean, Object],
    default: false,
  },
})

const setRowRef = (el, row) => {
  if (el && !row.was_read) {
    el.__dataRow = row.id
    observer.value.observe(el)
  }
}

const onIntersect = entries => {
  for (const entry of entries) {
    if (!entry.isIntersecting) {
      continue
    }
    ids.add(entry.target.__dataRow)
  }
}

function markModelRead() {
  if (!model.value?.data || ids.size === 0) return
  model.value.data = model.value.data.map(m => (ids.has(m.id) ? { ...m, was_read: 1 } : m))
  ids.clear()
  if (props.localHasUnread && typeof props.localHasUnread === 'object') {
    // ref из родителя (indicator) — обновляем флаг непрочитанного
    // eslint-disable-next-line vue/no-mutating-props
    props.localHasUnread.value = !!model.value.data.some(m => !m.was_read)
  }
}

async function loadAll() {
  try {
    showAll.value = true
    await refresh({ query: notificationQuery() })
  } catch (e) {
    console.error('loadAll error', e)
  }
}

// Текст нотификаций приходит готовым с backend (используется и для email),
// поэтому при смене Vue-локали нужен новый запрос.
watch(locale, () => {
  refresh({ query: notificationQuery() }).catch(e => console.error('locale refresh error', e))
})

onMounted(() => {
  observer.value = new IntersectionObserver(onIntersect, {
    root: null,
    rootMargin: '0px',
    threshold: 0.1,
  })

  refresh({ query: notificationQuery() })
})
onUnmounted(() => {
  observer.value.disconnect()
  if (ids.size > 0) {
    doRead()
    markModelRead()
    const currentUnread = unreadCount.value - ids.size
    // eslint-disable-next-line vue/no-mutating-props
    props.localHasUnread.value = Math.max(currentUnread, 0) > 0
  }
})
</script>

<i18n>
{
  "ru": {
    "unreadSummary": "{n} непрочитанное сообщение{scope} | {n} непрочитанных сообщения{scope} | {n} непрочитанных сообщений{scope}",
    "noUnreadSummary": "Нет непрочитанных сообщений{scope}",
    "scopeAll": " (до 1000)",
    "scopeRecent": " из последних 200",
    "allMessages": "Все сообщения",
    "badgeNew": "новое",
    "goToLink": "перейти",
    "loadingMessages": "Загрузка сообщений...",
    "noMessages": "Нет сообщений"
  },
  "en": {
    "unreadSummary": "{n} unread message{scope} | {n} unread messages{scope}",
    "noUnreadSummary": "No unread messages{scope}",
    "scopeAll": " (up to 1000)",
    "scopeRecent": " from the last 200",
    "allMessages": "All messages",
    "badgeNew": "new",
    "goToLink": "go to",
    "loadingMessages": "Loading messages...",
    "noMessages": "No messages"
  }
}
</i18n>
<style lang="scss">
.modal.modal-messages {
  display: flex !important;
  align-items: center;

  .modal-dialog {
    margin: auto;
    width: 50vw;
    max-width: 700px;
    min-width: 300px;

    .modal-content {
      height: 80vh;
      padding-bottom: 20px;
    }

    .modal-body {
      overflow: auto;
    }
  }
}

.one-mess {
  /* Радиус карточки обрезает метку — верхний правый край без «зубца» */
  .card {
    --mess-accent: #e1e1e1;
    overflow: hidden;
    border: 2px solid var(--mess-accent);
  }

  .warning {
    --mess-accent: #ffe69c;
  }

  .primary {
    --mess-accent: #9ec5fe;
  }

  .danger {
    --mess-accent: #f1aeb5;
  }

  .new-mess {
    float: right;
    display: block;
    margin: 0;
    padding: 0 5px;
    line-height: 1.25;
    background-color: var(--mess-accent);
    border-left: 2px solid var(--mess-accent);
    border-bottom: 2px solid var(--mess-accent);
    /* top-right = внутренний радиус карточки; bottom-left — скругление метки */
    border-radius: 0 calc(var(--bs-card-border-radius, 20px) - 2px) 0 8px;
  }

  .link-mess {
    color: var(--berry);
    text-decoration-color: var(--berry);

    &:hover {
      color: var(--berry-dark);
      text-decoration-color: rgba(255, 255, 255, 0);
    }
  }
}
</style>
